import { useEffect, useMemo, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import PageMeta from "@/components/PageMeta";
import { useSiteFlags } from "@/contexts/SiteFlagsContext";
import { slugify } from "@/lib/slug";
import HeartButton from "@/components/HeartButton";
import { toast } from "sonner";
import { Scroll, ArrowLeft, GlobeHemisphereWest } from "@phosphor-icons/react";

/**
 * HistoricalRegion — country-level SEO hub for historical recipes.
 *
 * URL: /historical/region/{country-slug}   (e.g. /historical/region/france)
 *
 * Purpose:
 *   - Rank for search intent like "old French recipes",
 *     "traditional Indian historical recipes", etc.
 *   - Create indexable pages that internally link back into the historical
 *     recipe detail pages (link-juice loop).
 *
 * We resolve the country slug back to the canonical country name by fetching
 * the facets list — DB stores "United States" but URL uses "united-states".
 * If no match is found we redirect to /historical to avoid a broken UX.
 */
export default function HistoricalRegion() {
  const { country: countrySlug } = useParams();
  const { flags, loading: flagsLoading } = useSiteFlags();
  const nav = useNavigate();
  const [country, setCountry] = useState(null);
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (flagsLoading) return;
    if (!flags.historical_recipes_enabled) { nav("/", { replace: true }); return; }
    (async () => {
      try {
        // 1. Resolve the country slug → proper name via facets.
        const facetsRes = await api.get("/historical/facets");
        const matches = (facetsRes.data.countries || []).find(
          (c) => slugify(c.name) === countrySlug,
        );
        if (!matches) {
          toast.error("Region not found");
          nav("/historical", { replace: true });
          return;
        }
        setCountry({ name: matches.name, count: matches.count });
        // 2. Fetch recipes for that country.
        const listRes = await api.get("/historical/recipes", {
          params: { country: matches.name, limit: 60, sort: "year_asc" },
        });
        setItems(listRes.data.items || []);
        setTotal(listRes.data.total || 0);
      } catch {
        toast.error("Couldn't load region page");
      } finally {
        setLoading(false);
      }
    })();
  }, [countrySlug, flags.historical_recipes_enabled, flagsLoading, nav]);

  const jsonLd = useMemo(() => {
    if (!country) return null;
    const listItems = items.map((r, i) => ({
      "@type": "ListItem",
      position: i + 1,
      url: `https://recipefiler.com/historical/${r.slug || r.historical_id}`,
      name: r.title,
    }));
    return [
      {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "Home", item: "https://recipefiler.com/" },
          { "@type": "ListItem", position: 2, name: "Historical Recipes", item: "https://recipefiler.com/historical" },
          { "@type": "ListItem", position: 3, name: country.name },
        ],
      },
      {
        "@context": "https://schema.org",
        "@type": "ItemList",
        name: `Historical recipes from ${country.name}`,
        description: `Historical recipes drawn from real ${country.name} public-domain cookbooks. Modernised for a modern kitchen.`,
        numberOfItems: listItems.length || total,
        itemListElement: listItems,
      },
    ];
  }, [country, items, total]);

  if (loading || !country) {
    return <div className="p-16 text-center text-rf-ink-soft" data-testid="historical-region-loading">Loading…</div>;
  }

  const title = `Historical recipes from ${country.name} — ${country.count} real dishes`;
  const description = `${country.count} historical recipes from ${country.name}, drawn from real public-domain cookbooks and modernised for a modern kitchen.`;
  const canonical = `https://recipefiler.com/historical/region/${countrySlug}`;

  return (
    <div className="min-h-screen bg-rf-cream" data-testid="historical-region-page">
      <PageMeta
        title={title}
        description={description}
        canonical={canonical}
        ogImage="/historical-hero.jpg"
        jsonLd={jsonLd}
      />

      {/* Hero */}
      <div className="bg-gradient-to-br from-amber-900/90 via-rf-ink to-rf-ink text-rf-cream py-12 px-6">
        <div className="max-w-5xl mx-auto">
          <Link
            to="/historical"
            className="inline-flex items-center gap-2 text-sm text-rf-amber hover:text-rf-cream mb-4"
            data-testid="historical-region-back"
          >
            <ArrowLeft size={16} weight="bold" /> All Historical Recipes
          </Link>
          <p className="overline !text-rf-amber mb-2 inline-flex items-center gap-2">
            <GlobeHemisphereWest size={14} weight="fill" /> {country.name}
          </p>
          <h1 className="font-display font-black text-4xl sm:text-5xl lg:text-6xl mb-4 leading-tight">
            Historical recipes from {country.name}.
          </h1>
          <p className="text-lg opacity-90 max-w-2xl">
            {country.count} recipe{country.count === 1 ? "" : "s"} drawn from real public-domain cookbooks — each one modernised so it works in a modern kitchen, without losing its soul.
          </p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-8">
        {items.length === 0 ? (
          <div className="text-center py-20 bg-white rounded-3xl border border-rf-ink/5">
            <Scroll size={48} className="mx-auto text-rf-ink-soft mb-4" />
            <p className="text-rf-ink-soft">No published recipes for {country.name} yet — check back soon.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="historical-region-grid">
            {items.map((r) => (
              <div key={r.historical_id} className="relative">
              <Link
                to={`/historical/${r.slug || r.historical_id}`}
                data-testid={`historical-region-card-${r.historical_id}`}
                className="block h-full bg-white rounded-3xl overflow-hidden border border-rf-ink/5 shadow-sm hover:shadow-lg transition-all hover:-translate-y-0.5"
              >
                <div className="aspect-[4/3] bg-gradient-to-br from-amber-800 to-rf-ink relative">
                  {r.image_url ? (
                    <img
                      src={r.image_url}
                      alt={r.title}
                      loading="lazy"
                      className="absolute inset-0 w-full h-full object-cover"
                      onError={(e) => { e.currentTarget.style.display = "none"; }}
                    />
                  ) : (
                    <div className="absolute inset-0 flex items-center justify-center opacity-10">
                      <Scroll size={80} weight="fill" className="text-rf-cream" />
                    </div>
                  )}
                  <span className="absolute top-3 left-3 bg-rf-cream text-rf-ink text-[10px] font-black uppercase tracking-widest px-2 py-1 rounded-full">
                    {r.year > 0 ? r.year : `${Math.abs(r.year)} BC`}
                  </span>
                </div>
                <div className="p-4">
                  <p className="text-[10px] uppercase tracking-widest text-rf-ink-soft mb-1">
                    {r.era_label || ""}
                  </p>
                  <h2 className="font-display font-black text-lg text-rf-ink leading-tight mb-2 line-clamp-2">
                    {r.title}
                  </h2>
                  <p className="text-xs text-rf-ink-soft italic line-clamp-2">
                    from <b className="not-italic">{r.source_book}</b> — {r.author}
                  </p>
                </div>
              </Link>
              <HeartButton kind="historical" id={r.historical_id} count={r.favourite_count} className="absolute bottom-3 right-3 z-10" />
              </div>
            ))}
          </div>
        )}

        <div className="text-center mt-12">
          <Link
            to="/historical"
            className="inline-flex items-center gap-2 text-sm font-black text-rf-orange hover:text-rf-ink"
            data-testid="historical-region-browse-all"
          >
            Browse all 50 historical recipes →
          </Link>
        </div>
      </div>
    </div>
  );
}
